import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { ClientResponse, IPagination, IQueryPerams } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import CustomizedSwitch from "../../components/Switch";
import { formatSnakeCaseToReadable } from "../../utils/common";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T, pagination?: IPagination) => void;
}

interface IAssetDistributionRejectionFilter {
  asset_brand?: string;
  asset_serial_number?: string;
  asset_embossment_number?: string;
  distribution_batch_number?: string;
  from_location?: string;
  to_location?: string;
  user_email?: string;
  reason?: string;
  search?: string;
  is_deleted?: boolean;
}

export const AssetDistributionRejectionFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet,
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<IAssetDistributionRejectionFilter>({
      asset_brand: "",
      asset_serial_number: "",
      asset_embossment_number: "",
      distribution_batch_number: "",
      from_location: "",
      to_location: "",
      user_email: "",
      reason: "",
      search: "",
    });

  const resetFilter = <K extends keyof IAssetDistributionRejectionFilter>(key: K) => {
    updateForm((p) => {
      const state = {
        ...p,
        [key]: { value: "" },
      };
      updateQuerySet((prev) => ({ ...prev, [key]: "" }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    updateQuerySet((p) => ({ ...p, ...data }));
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data, res.pagination);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.asset_brand?.value,
    form.asset_serial_number?.value,
    form.asset_embossment_number?.value,
    form.distribution_batch_number?.value,
    form.from_location?.value,
    form.to_location?.value,
    form.user_email?.value,
    form.reason?.value,
    form.search?.value,
    form.is_deleted
  ]);

  return (
    <Stack direction={"row"} justifyContent={"space-between"} width={"100%"}>
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading} closeOnoutSideClick>
          <Stack direction={"column"} spacing={2}>
            <SideBarSearchInput
              label="Asset Serial Number"
              placeholder="Search by serial number..."
              value={form.asset_serial_number?.value || ""}
              handleChange={updateInputField("asset_serial_number")}
            />
            <SideBarSearchInput
              label="Asset Brand"
              placeholder="Search by asset brand..."
              value={form.asset_brand?.value || ""}
              handleChange={updateInputField("asset_brand")}
            />
            <CustomizedSwitch
              title="deleted"
              onChange={updateInputField("is_deleted", "BOOLEAN")}
              value={form.is_deleted?.value ?? false}
            />
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFormValues())
            .filter(
              ([_, value]) =>
                (typeof value === "string" && value.trim() !== "") ||
                typeof value === "boolean"
            )
            .map(([key, value]) => (
              <FilterItemButton
                key={key}
                label={`By ${formatSnakeCaseToReadable(key)}`}
                tip={value.toString()}
                height={40}
                onClick={() => resetFilter(key as keyof IAssetDistributionRejectionFilter)}
              />
            ))}
        </Box>
      </Stack>
      <Box width={250}>
        <SideBarSearchInput
          placeholder="Search by embossment number..."
          value={form.asset_embossment_number?.value || ""}
          handleChange={updateInputField("asset_embossment_number")}
        />
      </Box>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570,
    },
  }),
};